import {useEffect, useState} from "react";
import {reportsApi} from "@/boxhub-api";
import {
    CollaborationByMonthDTO,
    CollaborationSummaryDTO,
    QuoteDetailDTO,
    QuotesByCompanyDTO,
    QuotesByMonthDTO,
    QuoteStatusByCompanyDTO,
    QuoteSupplierRequesterDTO,
    RequestDetailDTO,
    RequestsByCompanyDTO,
    RequestsByMonthDTO,
    RequestStatusByCompanyDTO
} from "@/api/data-contracts";

export interface ReportData {
    requestsByCompany: RequestsByCompanyDTO[];
    quotesByCompany: QuotesByCompanyDTO[];
    requestsByMonth: RequestsByMonthDTO[];
    quotesByMonth: QuotesByMonthDTO[];
    requestStatusByCompany: RequestStatusByCompanyDTO[];
    quoteStatusByCompany: QuoteStatusByCompanyDTO[];
    collaborationSummary: CollaborationSummaryDTO[];
    collaborationByMonth: CollaborationByMonthDTO[];
    requestsDetail: RequestDetailDTO[];
    quotesDetail: QuoteDetailDTO[];
    supplierRequester: QuoteSupplierRequesterDTO[];
}

interface UseReportDataResult {
    data: ReportData;
    loading: boolean;
    error: unknown;
}

const EMPTY_DATA: ReportData = {
    requestsByCompany: [],
    quotesByCompany: [],
    requestsByMonth: [],
    quotesByMonth: [],
    requestStatusByCompany: [],
    quoteStatusByCompany: [],
    collaborationSummary: [],
    collaborationByMonth: [],
    requestsDetail: [],
    quotesDetail: [],
    supplierRequester: []
};

/**
 * Custom hook that loads all report data for the given date range.
 *
 * @param from - ISO date string for the start of the range
 * @param to - ISO date string for the end of the range
 * @returns loading state, error, and all 11 report datasets
 */
const useReportData = (from: string, to: string): UseReportDataResult => {
    const [data, setData] = useState<ReportData>(EMPTY_DATA);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        let cancelled = false;

        const fetchAll = async () => {
            setLoading(true);
            setError(null);
            try {
                const [
                    requestsByCompanyRes,
                    quotesByCompanyRes,
                    requestsByMonthRes,
                    quotesByMonthRes,
                    requestStatusByCompanyRes,
                    quoteStatusByCompanyRes,
                    collaborationSummaryRes,
                    collaborationByMonthRes,
                    requestsDetailRes,
                    quotesDetailRes,
                    supplierRequesterRes
                ] = await Promise.all([
                    reportsApi.getRequestsByCompany({from, to}),
                    reportsApi.getQuotesByCompany({from, to}),
                    reportsApi.getRequestsByMonth({from, to}),
                    reportsApi.getQuotesByMonth({from, to}),
                    reportsApi.getRequestStatusByCompany({from, to}),
                    reportsApi.getQuoteStatusByCompany({from, to}),
                    reportsApi.getCollaborationSummary({from, to}),
                    reportsApi.getCollaborationByMonth({from, to}),
                    reportsApi.getRequestsDetail({from, to}),
                    reportsApi.getQuotesDetail({from, to}),
                    reportsApi.getQuoteSupplierRequester({from, to})
                ]);

                if (!cancelled) {
                    setData({
                        requestsByCompany: requestsByCompanyRes.data,
                        quotesByCompany: quotesByCompanyRes.data,
                        requestsByMonth: requestsByMonthRes.data,
                        quotesByMonth: quotesByMonthRes.data,
                        requestStatusByCompany: requestStatusByCompanyRes.data,
                        quoteStatusByCompany: quoteStatusByCompanyRes.data,
                        collaborationSummary: collaborationSummaryRes.data,
                        collaborationByMonth: collaborationByMonthRes.data,
                        requestsDetail: requestsDetailRes.data,
                        quotesDetail: quotesDetailRes.data,
                        supplierRequester: supplierRequesterRes.data
                    });
                }
            } catch (err: unknown) {
                if (!cancelled) {
                    setError(err);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchAll();

        return () => {
            cancelled = true;
        };
    }, [from, to]);

    return {data, loading, error};
};

export default useReportData;
